"use client"

import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import axios from "axios"
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
  Fade,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import { CalendarMonth, People, CardGiftcard, Stars, Notifications, Refresh } from "@mui/icons-material"

const Stats = ({ id }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const theme = useTheme()

  const fetchData = async () => {
    setLoading(true)
    let url = `/api/v2/partner/${id}/stats`
    if (startDate && endDate) {
      url += `?fromDate=${startDate}&toDate=${endDate}`
    }
    if (startDate && !endDate) {
      url += `?fromDate=${startDate}`
    }
    if (!startDate && endDate) {
      url += `?toDate=${endDate}`
    }
    try {
      const response = await axios.get(url)
      setData(response.data)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [id, startDate, endDate])

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ]

  // Custom colors for charts and cards
  const colors = {
    clients: theme.palette.primary.main,
    stores: "#FF6B6B",
    gifts: "#4ECDC4",
    announcements: "#FFD166",
    chartColors: ["#6E4CC5", "#FF6B6B", "#4ECDC4", "#FFD166", "#845EC2"],
  }

  const formatLargeNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M"
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K"
    }
    return num
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card sx={{ p: 1.5, boxShadow: theme.shadows[3], border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" color="text.primary">
            {label}
          </Typography>
          <Typography variant="body2" color={payload[0].color}>
            {`${payload[0].name}: ${payload[0].value}`}
          </Typography>
        </Card>
      )
    }
    return null
  }

  if (loading && !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ bgcolor: alpha(theme.palette.background.default, 0.8) }}
      >
        <CircularProgress size={60} thickness={4} color="primary" />
      </Box>
    )
  }

  const statCards = [
    {
      title: "Total Clients",
      value: data?.totalClients || 0,
      icon: <People />,
      color: theme.palette.primary.main,
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.2)} 100%)`,
    },
    {
      title: "Gift Orders",
      value: data?.totalGiftOrders || 0,
      icon: <CardGiftcard />,
      color: theme.palette.secondary.main,
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.2)} 100%)`,
    },
    {
      title: "Points Earned",
      value: formatLargeNumber(data?.totalPointsEarned || 0),
      icon: <Stars />,
      color: theme.palette.success.main,
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.2)} 100%)`,
    },
    {
      title: "Announcement Clicks",
      value: formatLargeNumber(data?.totalAnnouncementClicks || 0),
      icon: <Notifications />,
      color: theme.palette.warning.main,
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.2)} 100%)`,
    },
  ]

  // Convert gift orders by status to pie chart data
  const giftOrdersPieData =
    data?.totalGiftOrdersByStatus.map((item) => ({
      name: item.status,
      value: item.count,
    })) || []

  return (
    <Box sx={{ bgcolor: alpha(theme.palette.background.default, 0.03), minHeight: "100vh" }}>
      <Fade in={!loading} timeout={800}>
        <Grid container spacing={3} sx={{ p: 3 }}>
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                background: `linear-gradient(to right, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.light, 0.1)})`,
                backdropFilter: "blur(8px)",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
                  Partner Statistics Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Analyze performance metrics and track client engagement
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  alignItems: "center",
                }}
              >
                <TextField
                  type="date"
                  label="Start Date"
                  value={startDate}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setStartDate(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonth fontSize="small" color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: "100%", sm: 200 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={endDate}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setEndDate(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonth fontSize="small" color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: "100%", sm: 200 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                    },
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={fetchData}
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    boxShadow: theme.shadows[1],
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Stack>
            </Card>
          </Grid>

          {/* Stat Cards */}
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  background: stat.bgGradient,
                  transition: "all 0.3s ease",
                  overflow: "hidden",
                  position: "relative",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                    "& .stat-icon": {
                      transform: "scale(1.1)",
                    },
                  },
                }}
              >
                <Box
                  className="stat-icon"
                  sx={{
                    position: "absolute",
                    right: -10,
                    top: -10,
                    opacity: 0.2,
                    fontSize: "5rem",
                    color: stat.color,
                    transition: "transform 0.3s ease",
                    transform: "scale(1)",
                  }}
                >
                  {stat.icon}
                </Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {stat.title}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="text.primary">
                  {stat.value}
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    width: "40%",
                    height: 4,
                    borderRadius: 2,
                    bgcolor: stat.color,
                  }}
                />
              </Card>
            </Grid>
          ))}

          {/* Charts and Tables */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                p: 3,
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <People color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="medium">
                  Top Clients by Points
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        Client Name
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                      >
                        Points
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.topClientsByPoints.map((client, index) => (
                      <TableRow
                        key={client.Client.name}
                        sx={{
                          "&:nth-of-type(odd)": { bgcolor: alpha(theme.palette.background.default, 0.3) },
                          "&:hover": { bgcolor: alpha(theme.palette.primary.light, 0.1) },
                          transition: "background-color 0.2s",
                        }}
                      >
                        <TableCell
                          sx={{ borderLeft: index === 0 ? `4px solid ${theme.palette.primary.main}` : "none" }}
                        >
                          {client.Client.name}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={Number.parseFloat(client.points).toFixed(2)}
                            size="small"
                            sx={{
                              fontWeight: "bold",
                              bgcolor: index === 0 ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                              color: index === 0 ? theme.palette.primary.main : "inherit",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                p: 3,
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Notifications color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="medium">
                  Top Announcements
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data?.topAnnouncementsByClicks}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                    <XAxis
                      dataKey="text"
                      tickFormatter={(value) => (value.length > 10 ? value.substring(0, 10) + "..." : value)}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    <Bar dataKey="click_count" name="Clicks" radius={[4, 4, 0, 0]} barSize={30}>
                      {data?.topAnnouncementsByClicks.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                p: 3,
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <CardGiftcard color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="medium">
                  Gift Orders by Status
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={giftOrdersPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      innerRadius={40}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {giftOrdersPieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={colors.chartColors[index % colors.chartColors.length]}
                          stroke={theme.palette.background.paper}
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Fade>
    </Box>
  )
}

Stats.propTypes = {
  id: PropTypes.string.isRequired,
}

export default Stats

